/**
 * Sacar una capa del widget fuera de lo que la recorta, sin que pierda su aspecto.
 *
 * EL PROBLEMA. El calendario se dibuja dentro del formulario, colocado en absoluto. Eso vale
 * mientras nadie por encima recorte, que es lo que pasa en la portada de renfeviajes. En una
 * landing cualquiera no: medido en la home de Corporate Benefits, la sección del hero lleva
 * `overflow: hidden` y acaba 158px antes que el calendario, así que la mitad de septiembre no
 * se ve. Y no vale con `position: fixed`: la tarjeta que envuelve al buscador lleva
 * `backdrop-filter`, y un ancestro con filtro es contenedor de bloque hasta para un fijo — el
 * calendario se iba 418px abajo y seguía recortado.
 *
 * LO QUE NO SE PUEDE HACER. Quitarle el `overflow: hidden` a la landing: ese recorte está ahí
 * a propósito (la foto del tren se sale del hero) y no es nuestro para tocarlo.
 *
 * LO QUE SE HACE. Mover la capa al contenedor de overlays, que cuelga del `<body>` y no lo
 * recorta nadie... llevándose una COPIA VACÍA DE SUS ANCESTROS. Hace falta porque el CSS del
 * buscador no cuelga de la clase de la capa sino de la cadena entera —
 * `.rvb-w [data-rvb="form"]×8 .rvb-calendarActive .rvb-datepicker-container` — y sacada a pelo
 * se vería un calendario sin un solo estilo. Con la cadena clonada, las mismas reglas siguen
 * aplicando y no hay que duplicar ni una.
 *
 * Y se hace SÓLO cuando hace falta: si nadie recorta, la capa se queda donde estaba y todo
 * sigue exactamente igual. En la portada no llega a plantearse.
 */

/**
 * ¿Hay alguien por encima que la recorte o la encierre?
 *
 * Dos cosas distintas, y las dos se arreglan igual —sacándola fuera—, así que se preguntan a la
 * vez:
 *
 *   RECORTAR. `overflow` distinto de visible, `clip-path` o `contain: paint`. Lo que se sale de
 *   ahí no se ve: en la home de Corporate Benefits el hero se comía media hoja del calendario.
 *
 *   ENCERRAR. Un ancestro que crea CONTEXTO DE APILAMIENTO —un filtro, una transformación, una
 *   opacidad por debajo de 1, `isolation`, `will-change`— mete todo lo que lleva dentro en su
 *   propia baraja: por mucho z-index que se le ponga a la capa, no puede pasar por delante de
 *   nada que esté fuera. Medido en las fichas de ciudad, cuya tarjeta lleva `backdrop-filter`:
 *   el banner de cookies de la landing, con un z-index de 1000, se ponía delante del calendario
 *   y tapaba la fila de los días elegidos. Es el mismo mecanismo que impide que un
 *   `position: fixed` escape de ahí.
 *
 * Se para en el `<body>`: si es el body el que recorta o encierra, moverla no arregla nada.
 *
 * @param {Element} desde
 * @returns {boolean}
 */
export function alguienRecorta (desde) {
  for (let n = desde.parentElement; n && n !== document.body; n = n.parentElement) {
    const cs = getComputedStyle(n)
    // recorta
    if (cs.overflow !== 'visible' || cs.overflowX !== 'visible' || cs.overflowY !== 'visible') return true
    if (cs.clipPath !== 'none') return true
    if (/paint|strict|content/.test(cs.contain)) return true
    // encierra
    if (cs.filter !== 'none' || cs.backdropFilter !== 'none') return true
    if (cs.transform !== 'none' || cs.perspective !== 'none') return true
    if (cs.isolation === 'isolate' || cs.mixBlendMode !== 'normal') return true
    if (/transform|filter|opacity/.test(cs.willChange)) return true
    if (Number(cs.opacity) < 1) return true
  }
  return false
}

/**
 * Copia vacía de un elemento: su etiqueta, sus clases y sus `data-*`, sin hijos.
 *
 * El `id` NO se copia a propósito: el markup del buscador trae ids del original y dos nodos
 * con el mismo id en la página es un estropicio que se paga en otro sitio.
 */
function cascaraDe (elemento) {
  const copia = document.createElement(elemento.tagName)
  for (const atributo of elemento.attributes) {
    if (atributo.name === 'id') continue
    copia.setAttribute(atributo.name, atributo.value)
  }
  return copia
}

/**
 * Saca `capa` a `destino` con la cadena de ancestros clonada hasta `tope` (sin incluirlo).
 *
 * Devuelve una función que la deja donde estaba. Mientras está fuera, la capa se coloca en
 * absoluto sobre las mismas coordenadas de pantalla que ocupaba, así que al cliente no se le
 * mueve nada: sólo deja de estar recortada.
 *
 * @param {Element} capa      la que hay que sacar (el contenedor del calendario)
 * @param {Element} tope      la raíz del widget: se clona hasta aquí, sin incluirla
 * @param {Element} destino   el contenedor de overlays, que ya cuelga del <body>
 * @param {{colocar?: boolean}} [opciones]  `colocar: false` deja la colocación al CSS — es lo
 *   que hace falta en el móvil, donde el calendario va a pantalla completa y unas coordenadas
 *   lo dejarían convertido en una tira de días sobre el formulario.
 * @returns {() => void}      devolverla a su sitio
 */
export function sacarFuera (capa, tope, destino, opciones) {
  const colocar = !opciones || opciones.colocar !== false
  const padre = capa.parentElement
  const hermano = capa.nextSibling
  const antes = capa.getBoundingClientRect()

  // La cadena, de dentro hacia fuera, sin el tope.
  const cadena = []
  for (let n = padre; n && n !== tope; n = n.parentElement) cadena.push(n)

  // Y clonada de fuera hacia dentro, para poder ir colgando una de otra.
  let raizPortal = null
  let hueco = null
  for (let i = cadena.length - 1; i >= 0; i--) {
    const copia = cascaraDe(cadena[i])
    if (hueco) hueco.appendChild(copia); else raizPortal = copia
    hueco = copia
  }
  if (!hueco) { raizPortal = document.createElement('div'); hueco = raizPortal }

  // El portal no ocupa sitio: lo que se ve es la capa, colocada en absoluto.
  raizPortal.setAttribute('data-rvb-portal', '')
  raizPortal.style.position = 'absolute'
  raizPortal.style.top = '0'
  raizPortal.style.left = '0'
  raizPortal.style.width = '0'
  raizPortal.style.height = '0'
  // Por encima de cualquier cosa de la landing, que es el mismo trato que reciben las
  // sugerencias y los modales. Medido sin esto: el banner de cookies de Corporate Benefits
  // --`position: fixed`, z-index 1000-- se ponía delante del calendario y tapaba la fila de
  // los días elegidos.
  raizPortal.style.zIndex = '2147483000'

  hueco.appendChild(capa)
  destino.appendChild(raizPortal)

  // La regla que la enseña cuelga de un ancestro que aquí es una cáscara: como la cáscara
  // lleva las mismas clases, sigue valiendo. El ancho sí se fija: sin el `right` del original,
  // la caja se encogería al contenido.
  if (colocar) {
    capa.style.setProperty('position', 'absolute', 'important')
    capa.style.setProperty('right', 'auto', 'important')
    capa.style.setProperty('bottom', 'auto', 'important')
    capa.style.setProperty('width', antes.width + 'px', 'important')
    capa.style.setProperty('top', '0', 'important')
    capa.style.setProperty('left', '0', 'important')

    // Dónde ha caído puesta en 0,0 — que depende de quién sea su contenedor de bloque, y eso no
    // se sabe de antemano. La diferencia con donde estaba es lo que hay que corregir: así queda
    // en el mismo sitio de la pantalla sea cual sea el contenedor.
    const enCero = capa.getBoundingClientRect()
    capa.style.setProperty('top', (antes.top - enCero.top) + 'px', 'important')
    capa.style.setProperty('left', (antes.left - enCero.left) + 'px', 'important')
  }

  return function devolver () {
    ;['position', 'top', 'left', 'right', 'bottom', 'width'].forEach((p) => capa.style.removeProperty(p))
    if (hermano && hermano.parentNode === padre) padre.insertBefore(capa, hermano)
    else padre.appendChild(capa)
    if (raizPortal.parentNode) raizPortal.parentNode.removeChild(raizPortal)
  }
}
